import { AlertTriangle } from "lucide-react";
import { EPOCH_VERSION } from "../../../lib/epoch-version";
import { VersionInfoPopover } from "../VersionInfoPopover";
import { FooterBrowserControls } from "./FooterBrowserControls";
import {
  FOOTER_DATA_FONT_STYLE,
  FOOTER_HEADING_FONT_STYLE,
  type FooterPanelTab,
  type FooterToolbarProps,
} from "./footer-toolbar-model";
import { FooterStatusGroup, useFooterStatusGroups } from "./FooterStatusGroup";

interface FooterStripProps extends FooterToolbarProps {
  activeTab: FooterPanelTab;
  errorCount: number;
  fetchStatus: string | null;
  openPanel: boolean;
  openTab: (tab: FooterPanelTab) => void;
  refreshing: boolean;
  updateInstalling: boolean;
}

export function FooterStrip(props: FooterStripProps) {
  const status = useFooterStatusGroups();
  return (
    <div className="flex h-7 items-center gap-3 px-3">
      <div style={FOOTER_DATA_FONT_STYLE}>
        <VersionInfoPopover version={EPOCH_VERSION} />
      </div>
      <div
        className={`overflow-hidden transition-[max-width,opacity,transform] duration-300 ${
          props.errorCount > 0
            ? "max-w-[42px] translate-y-0 opacity-100"
            : "pointer-events-none max-w-0 translate-y-1 opacity-0"
        }`}
      >
        <button
          type="button"
          onClick={() => props.openTab("errors")}
          className={`relative flex h-5 w-5 items-center justify-center text-[10px] transition-colors ${
            props.openPanel && props.activeTab === "errors"
              ? "text-red-400"
              : "text-red-400/60 hover:text-red-400"
          }`}
          title="Error Log"
          aria-label="Open error log"
        >
          <AlertTriangle className="h-3 w-3" />
          <span
            className="absolute -right-1.5 -top-1 text-[7px] leading-none text-red-400 tabular-nums"
            style={FOOTER_DATA_FONT_STYLE}
          >
            {props.errorCount > 9 ? "!" : props.errorCount}
          </span>
        </button>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <FooterBrowserControls {...props} />
        {props.compactLevel !== 0 || !props.updateInstalling ? null : (
          <FooterActivity label="Installing update..." active />
        )}
        {props.compactLevel !== 0 || !props.fetchStatus ? null : (
          <FooterActivity label={props.fetchStatus} active={props.refreshing} />
        )}
        {props.compactLevel !== 0 ? null : (
          <>
            <span
              className="shrink-0 text-[10px] font-medium tracking-normal text-[var(--fintheon-accent)]/55"
              style={FOOTER_HEADING_FONT_STYLE}
            >
              Priced In Capital
            </span>
            <div className="h-3.5 w-px bg-[var(--fintheon-accent)]/10" />
          </>
        )}
        <div className="flex shrink-0 items-center gap-3">
          <FooterStatusGroup
            label="Cloud"
            status={status.cloudStatus}
            items={status.cloud}
          />
          <FooterStatusGroup
            label="TradingView"
            status={status.tradingViewStatus}
            items={status.tradingView}
          />
        </div>
      </div>
    </div>
  );
}

function FooterActivity({ label, active }: { label: string; active: boolean }) {
  return (
    <>
      <div className="flex shrink-0 items-center gap-1.5">
        {active ? (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[var(--fintheon-accent)]" />
        ) : null}
        <span
          className="text-[9px] uppercase tracking-[0.15em] text-[var(--fintheon-accent)]/70"
          style={FOOTER_HEADING_FONT_STYLE}
        >
          {label}
        </span>
      </div>
      <div className="h-3.5 w-px bg-[var(--fintheon-accent)]/10" />
    </>
  );
}
